import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SettingsIcon from '@material-ui/icons/Settings';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import { setProvider, getCurrentProvider, PROVIDERS } from './comicDataProvider';

const useStyles = makeStyles((theme) => ({
  settingsButton: {
    margin: theme.spacing(1),
  },
  formControl: {
    margin: theme.spacing(2, 0),
    minWidth: 200,
  },
  infoBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  infoBoxInfo: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
  },
  infoBoxWarning: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

/**
 * Settings dialog for configuring comic data provider
 */
const ProviderSettings = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(getCurrentProvider());
  const [showInfo, setShowInfo] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setSelectedProvider(getCurrentProvider());
  };

  const handleClose = () => {
    setOpen(false);
    setShowInfo(false);
  };

  const handleSave = () => {
    setProvider(selectedProvider);
    
    // Show info message if Comic Vine is selected without API key
    if (selectedProvider === PROVIDERS.COMIC_VINE) {
      const apiKey = process.env.REACT_APP_COMIC_VINE_API_KEY;
      if (!apiKey || apiKey === 'YOUR_COMIC_VINE_API_KEY_HERE') {
        setShowInfo(true);
        return;
      }
    }
    
    handleClose();
    // Reload page to apply changes
    window.location.reload();
  };

  const getProviderDescription = (provider) => {
    switch (provider) {
      case PROVIDERS.COMIC_VINE:
        return 'Comprehensive database covering Marvel, DC, Image, Dark Horse, IDW, and more. Requires free API key.';
      case PROVIDERS.MARVEL:
        return 'Legacy Marvel API (deprecated - may not work). Marvel characters only.';
      case PROVIDERS.MOCK:
        return 'Mock data provider for testing. No real data will be fetched.';
      default:
        return '';
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SettingsIcon />}
        onClick={handleOpen}
        className={classes.settingsButton}
      >
        Data Source Settings
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Comic Data Source Settings</DialogTitle>
        <DialogContent>
          <FormControl className={classes.formControl} fullWidth>
            <InputLabel id="provider-select-label">Data Provider</InputLabel>
            <Select
              labelId="provider-select-label"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <MenuItem value={PROVIDERS.COMIC_VINE}>Comic Vine API (Recommended)</MenuItem>
              <MenuItem value={PROVIDERS.MARVEL}>Marvel API (Deprecated)</MenuItem>
              <MenuItem value={PROVIDERS.MOCK}>Mock Provider (Testing)</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" color="textSecondary" className={classes.infoBox}>
            {getProviderDescription(selectedProvider)}
          </Typography>

          {selectedProvider === PROVIDERS.COMIC_VINE && (
            <Paper className={`${classes.infoBox} ${classes.infoBoxInfo}`}>
              <div className={classes.iconContainer}>
                <InfoIcon color="primary" />
                <Typography variant="body2">
                  <strong>Setup Required:</strong> Comic Vine requires a free API key.
                  <br />
                  1. Get your key at{' '}
                  <a 
                    href="https://comicvine.gamespot.com/api/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={classes.link}
                  >
                    Comic Vine API
                  </a>
                  <br />
                  2. Add to .env file: <code>REACT_APP_COMIC_VINE_API_KEY=your_key</code>
                  <br />
                  3. Restart the application
                  <br />
                  <br />
                  See{' '}
                  <a 
                    href="https://github.com/hogsrule/Comics/blob/main/COMIC_DATA_SOURCES.md" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={classes.link}
                  >
                    COMIC_DATA_SOURCES.md
                  </a>
                  {' '}for detailed instructions.
                </Typography>
              </div>
            </Paper>
          )}

          {showInfo && (
            <Paper className={`${classes.infoBox} ${classes.infoBoxWarning}`}>
              <div className={classes.iconContainer}>
                <WarningIcon style={{ color: '#ff9800' }} />
                <Typography variant="body2">
                  <strong>API Key Not Configured!</strong>
                  <br />
                  Please configure your Comic Vine API key before using this provider.
                  See the documentation for setup instructions.
                </Typography>
              </div>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save & Reload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProviderSettings;
